import { useState } from 'react';
import { Eyebrow } from '../components/primitives';

export const Testimonials = () => {
  const quotes = [
    {
      q: 'An absolute game-changer for automating TTRPG rulesets. Intuitive, highly modular, brilliantly expandable — truly a genius innovation in the TTRPG space.',
      who: 'Crow Brain Games',
      role: 'Publisher',
    },
    {
      q: 'Quest Bound has empowered me to craft personalized, automated character sheets for tabletop RPGs that only exist in print. I wholeheartedly endorse this app.',
      who: 'Kazelob',
      role: 'Game Master',
    },
    {
      q: 'A powerful logic system that lets you customize every aspect of your game. It has become a must-have tool for any game creator or DM.',
      who: 'Biz',
      role: 'Game Designer',
    },
  ];
  return (
    <section
      className="qb-section-tight"
      style={{ padding: '120px 40px', borderBottom: '1px solid var(--qb-border-soft)' }}>
      <div style={{ maxWidth: 1280, margin: '0 auto' }}>
        <div style={{ marginBottom: 48 }}>
          <Eyebrow>FROM THE PRESS</Eyebrow>
          <h2
            className="qb-section-h2"
            style={{
              fontFamily: 'var(--qb-font-display)',
              textTransform: 'uppercase',
              fontSize: 56,
              lineHeight: 0.95,
              letterSpacing: '-0.015em',
              margin: '14px 0 0',
              maxWidth: 900,
              textWrap: 'balance',
            }}>
            Built by creators, used by creators.
          </h2>
        </div>

        <div className="qb-testimonials-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 22 }}>
          {quotes.map((q, i) => (
            <div
              key={i}
              style={{
                background: 'var(--qb-ink-800)',
                border: '1px solid var(--qb-border-strong)',
                borderRadius: 6,
                padding: '28px 26px',
                display: 'flex',
                flexDirection: 'column',
                gap: 18,
                position: 'relative',
              }}>
              <div
                style={{
                  fontFamily: 'var(--qb-font-display)',
                  fontSize: 56,
                  color: 'var(--qb-orange-500)',
                  lineHeight: 0.4,
                  height: 24,
                }}>
                “
              </div>
              <p
                style={{
                  fontFamily: 'var(--qb-font-body)',
                  fontStyle: 'italic',
                  fontSize: 16,
                  lineHeight: 1.5,
                  color: 'var(--qb-fg)',
                  margin: 0,
                  flexGrow: 1,
                  textWrap: 'pretty',
                }}>
                {q.q}
              </p>
              <div
                style={{
                  borderTop: '1px solid var(--qb-border-soft)',
                  paddingTop: 14,
                }}>
                <div
                  style={{
                    fontFamily: 'var(--qb-font-display)',
                    fontSize: 12,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    color: 'var(--qb-fg)',
                  }}>
                  {q.who}
                </div>
                <div
                  style={{
                    fontFamily: 'var(--qb-font-body)',
                    fontSize: 12,
                    color: 'var(--qb-fg-muted)',
                    marginTop: 2,
                  }}>
                  {q.role}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export const FAQ = () => {
  const items: { q: string; a: string | string[] }[] = [
    {
      q: 'Is the engine really free?',
      a: 'Yes. The Quest Bound engine is free and open source on GitHub. Build any Ruleset, play it at your table, fork the code. The Publisher Program is a separate, optional layer for creators who want to sell.',
    },
    {
      q: 'What does the Publisher Program cover?',
      a: 'Everything you need to run a TTRPG business on Quest Bound: a custom storefront, Stripe payouts, the sales + activity dashboard, customer accounts, refund handling, discount codes, and priority support. Players who own your game can host multiplayer sessions together — virtual game nights, synced rolls, shared campaigns — at no extra cost.',
    },
    {
      q: 'Does Quest Bound replace my virtual tabletop?',
      a: 'No. Quest Bound runs the rules and tracks the math. It plays alongside any VTT — Roll20, Foundry, Owlbear Rodeo — and just as well at a physical table with paper maps and real dice.',
    },
    {
      q: 'What systems can I build?',
      a: 'Any of them. Quest Bound is system-agnostic — D20, PbtA, OSR, FATE, your own homebrew. The engine is a set of primitives (attributes, archetypes, charts, scripts) that compose into whatever ruleset you need.',
    },
    {
      q: 'Can players use Quest Bound without paying?',
      a: 'Yes. Players access the games they own — paid or free — from any device. The Publisher Program covers creator infrastructure; players never pay Quest Bound directly.',
    },
    {
      q: 'Do I need to know how to code?',
      a: 'No. The engine handles most rule logic out of the box. QBScript is there if you want full control — automate complex interactions, custom dice, conditional rules. Easy to start, limitless to master.',
    },
    {
      q: 'Does Quest Bound use AI?',
      a: [
        'No. Quest Bound makes no use of AI. The platform uses a classical programming approach and relies on a custom-built framework to power the automation of rulesets.',
        'Whether publishers include AI generated content in their rulesets is up to them. Doing so does not violate the terms of service.',
      ],
    },
  ];
  const [open, setOpen] = useState<number>(0);
  return (
    <section
      id="faq"
      className="qb-section-tight"
      style={{
        padding: '120px 40px',
        borderBottom: '1px solid var(--qb-border-soft)',
        background: 'var(--qb-ink-800)',
      }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 56 }}>
          <Eyebrow>QUESTIONS</Eyebrow>
          <h2
            className="qb-section-h2"
            style={{
              fontFamily: 'var(--qb-font-display)',
              textTransform: 'uppercase',
              fontSize: 60,
              lineHeight: 0.95,
              letterSpacing: '-0.015em',
              margin: '16px 0 0',
            }}>
            The usual ones, answered.
          </h2>
        </div>
        <div className="qb-faq-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 48px' }}>
          {items.map((it, i) => (
            <div
              key={i}
              style={{
                borderTop: '1px solid var(--qb-border-strong)',
                padding: '22px 0',
              }}>
              <div
                onClick={() => setOpen(open === i ? -1 : i)}
                style={{
                  cursor: 'pointer',
                  fontFamily: 'var(--qb-font-display)',
                  textTransform: 'uppercase',
                  fontSize: 16,
                  letterSpacing: '0.04em',
                  lineHeight: 1.3,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 16,
                  color: 'var(--qb-fg)',
                }}>
                <span style={{ textWrap: 'balance' }}>{it.q}</span>
                <span
                  style={{
                    color: 'var(--qb-orange-500)',
                    fontSize: 22,
                    lineHeight: 0.6,
                    transition: 'transform 200ms var(--qb-ease-standard)',
                    transform: open === i ? 'rotate(45deg)' : 'rotate(0)',
                    display: 'inline-block',
                    width: 22,
                    textAlign: 'center',
                  }}>
                  +
                </span>
              </div>
              <div
                style={{
                  maxHeight: open === i ? 480 : 0,
                  overflow: 'hidden',
                  transition:
                    'max-height 360ms var(--qb-ease-standard), opacity 200ms var(--qb-ease-standard)',
                  opacity: open === i ? 1 : 0,
                }}>
                {(Array.isArray(it.a) ? it.a : [it.a]).map((para, pi) => (
                  <p
                    key={pi}
                    style={{
                      fontFamily: 'var(--qb-font-body)',
                      fontSize: 15,
                      lineHeight: 1.65,
                      color: 'var(--qb-fg-muted)',
                      margin: pi === 0 ? '14px 0 0' : '12px 0 0',
                      maxWidth: 520,
                    }}>
                    {para}
                  </p>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
